import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useConfigSection } from '../utils/useConfig';
import { useCookies } from '../context/CookieContext';

// Ikony Material UI
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import InfoIcon from '@mui/icons-material/Info';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SocialMediaIcons from './SocialMediaIcons';

const Footer = React.forwardRef<HTMLDivElement>((props, ref) => {
  const theme = useConfigSection('theme');
  const company = useConfigSection('company');
  const contact = useConfigSection('contact');
  const ui = useConfigSection('ui');
  const { openCookieModal } = useCookies();

  // Definicja animacji
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        when: 'beforeChildren',
      },
    },
  };

  const childVariants = {
    hidden: {
      opacity: 0,
      y: 10,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  // Dynamiczne style bazujące na konfiguracji
  const bgGradient = `bg-gradient-to-b from-${theme.colors.background}/50 to-${theme.colors.background}/80`;
  const primaryTextColor = `text-${theme.colors.primary}`;
  const primaryBorderColor = `border-${theme.colors.primary}`;
  const primaryHoverColor = `hover:text-${theme.colors.primary}`;
  const primaryLightBgColor = `bg-${theme.colors.primary}/10`;

  // Dane sekcji pobierane z konfiguracji
  const usefulLinks = ui.footer?.quickLinks || [
    {
      label: 'Katalog produktów',
      path: '/szukaj',
    },
    {
      label: 'Regulamin',
      path: '/regulamin',
    },
    {
      label: 'O nas',
      path: '/o-nas',
    },
    {
      label: 'Kontakt',
      path: '/kontakt',
    },
  ];

  // Mapowanie ikon do linków
  const getIconForLink = (path: string) => {
    if (path.includes('szuka')) return <ShoppingCartIcon fontSize="small" />;
    if (path.includes('regulamin')) return <MenuBookIcon fontSize="small" />;
    if (path.includes('o-nas')) return <InfoIcon fontSize="small" />;
    if (path.includes('kontakt')) return <ContactPageIcon fontSize="small" />;
    return <KeyboardArrowRightIcon fontSize="small" />;
  };

  return (
    <motion.footer
      className={`${bgGradient} pt-16 border-t border-gray-200`}
      ref={ref}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={containerVariants}
    >
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
          {/* O firmie */}
          <motion.div variants={childVariants} className="flex flex-col">
            <div className="flex items-center mb-6">
              <div
                className={`h-12 w-12 rounded-xl ${primaryLightBgColor} flex items-center justify-center ${primaryTextColor} mr-3`}
              >
                {company.logo ? (
                  <img src={company.logo} alt={company.name} className="h-8" />
                ) : (
                  <span className="text-2xl font-bold">
                    {company.name.charAt(0)}
                  </span>
                )}
              </div>
              <h2 className="text-xl font-bold">{company.name}</h2>
            </div>
            <p className="text-gray-600 mb-6">{company.about.shortInfo}</p>

            {/* Social media */}
            <SocialMediaIcons />
          </motion.div>

          {/* Przydatne linki */}
          {ui.footer?.showQuickLinks && (
            <motion.div variants={childVariants}>
              <h3
                className={`text-lg font-bold text-gray-900 mb-5 pb-2 border-b ${primaryBorderColor}`}
              >
                Przydatne linki
              </h3>
              <ul className="space-y-3">
                {usefulLinks.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.path}
                      className={`flex items-center text-gray-600 ${primaryHoverColor} transition-colors group`}
                    >
                      <span className="text-gray-400 mr-2 group-hover:text-inherit">
                        {getIconForLink(link.path)}
                      </span>
                      <span>{link.label}</span>
                      <motion.span
                        className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity"
                        initial={{ x: -5 }}
                        animate={{ x: 0 }}
                        transition={{ type: 'spring', stiffness: 300 }}
                      >
                        <KeyboardArrowRightIcon fontSize="small" />
                      </motion.span>
                    </a>
                  </li>
                ))}
              </ul>

              <div
                className={`mt-8 ${primaryLightBgColor} rounded-lg p-4 border ${primaryBorderColor}/20`}
              >
                <h4 className={`font-medium ${primaryTextColor} mb-1`}>
                  Obsługa klienta
                </h4>
                <p className="text-sm text-gray-700">
                  Nasi konsultanci są dostępni od poniedziałku do piątku w
                  godzinach 7:30-15:00
                </p>
              </div>
            </motion.div>
          )}

          {/* Skontaktuj się z nami */}
          <motion.div variants={childVariants}>
            <h3
              className={`text-lg font-bold text-gray-900 mb-5 pb-2 border-b ${primaryBorderColor}`}
            >
              Skontaktuj się z nami
            </h3>
            <ul className="space-y-4">
              {contact.email && (
                <li>
                  <a
                    href={`mailto:${contact.email}`}
                    className={`flex items-center text-gray-600 ${primaryHoverColor} transition-colors group p-2 -ml-2 hover:bg-gray-50 rounded-md`}
                  >
                    <span
                      className={`w-9 h-9 rounded-full ${primaryLightBgColor} ${primaryTextColor} flex items-center justify-center mr-3`}
                    >
                      <EmailIcon fontSize="small" />
                    </span>
                    <span>{contact.email}</span>
                  </a>
                </li>
              )}
              {contact.phone && (
                <li>
                  <a
                    href={`tel:${contact.phone}`}
                    className={`flex items-center text-gray-600 ${primaryHoverColor} transition-colors group p-2 -ml-2 hover:bg-gray-50 rounded-md`}
                  >
                    <span
                      className={`w-9 h-9 rounded-full ${primaryLightBgColor} ${primaryTextColor} flex items-center justify-center mr-3`}
                    >
                      <PhoneIcon fontSize="small" />
                    </span>
                    <span>{contact.phone}</span>
                  </a>
                </li>
              )}
            </ul>

            <div className="mt-6">
              <p className="text-sm text-gray-500">
                Chętnie odpowiemy na Twoje pytania związane z produktami i
                ofertą naszego sklepu.
              </p>
            </div>
          </motion.div>

          {/* Adres */}
          <motion.div variants={childVariants}>
            <h3
              className={`text-lg font-bold text-gray-900 mb-5 pb-2 border-b ${primaryBorderColor}`}
            >
              Adres firmy
            </h3>
            <div className="flex">
              <span
                className={`w-9 h-9 rounded-full ${primaryLightBgColor} ${primaryTextColor} flex items-center justify-center mr-3 flex-shrink-0`}
              >
                <LocationOnIcon fontSize="small" />
              </span>
              <div>
                <p className="text-gray-600">
                  <span className="font-medium">{company.name}</span>
                  <br />
                  ul. {contact.address.street}
                  <br />
                  {contact.address.postal} {contact.address.city}
                  <br />
                  {contact.address.country}, {contact.address.region}
                </p>

                {contact.googleMaps && (
                  <a
                    href={contact.googleMaps}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center mt-3 ${primaryTextColor} hover:underline`}
                  >
                    <span>Zobacz na mapie</span>
                    <KeyboardArrowRightIcon fontSize="small" />
                  </a>
                )}
              </div>
            </div>

            {contact.googleMaps && (
              <div className="mt-8 rounded-lg overflow-hidden border border-gray-200">
                <iframe
                  src={contact.googleMaps}
                  width="100%"
                  height="150"
                  style={{ border: 0 }}
                  loading="lazy"
                  title="Mapa lokalizacji"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            )}
          </motion.div>
        </div>

        <motion.div
          variants={childVariants}
          className="border-t border-gray-200 mt-12 py-8 text-center"
        >
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-500 text-sm">
              &copy; {new Date().getFullYear()} {company.name} - Wszelkie prawa
              zastrzeżone
            </p>
            <div className="flex space-x-4 mt-4 md:mt-0 text-xs text-gray-500">
              <Link to="/regulamin" className={primaryHoverColor}>
                Regulamin
              </Link>
              <button 
                onClick={openCookieModal} 
                className={`${primaryHoverColor} bg-transparent border-none cursor-pointer text-xs`}
              >
                Ustawienia plików cookies
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.footer>
  );
});

Footer.displayName = 'Footer';

export default Footer;
